import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import '../QueuePage.scss'
import QueueTableSortHeader from './QueueTableSortHeader';
import QueueTableColumnHeader from './QueueTableColumnHeader';
import QueueTableNoResults from './QueueTableNoResults';
import QueueTableRow from './QueueTableRow';
import QueueTableLoader from './QueueTableLoader';
import QueueTablePages from './QueueTablePages';
import { Permissions } from '../../../app/services/Permissions';

const QueueTable = (props) => {
    const [headerCheckboxState, setHeaderCheckboxState] = useState('unchecked');
    const PAGE_SELECTIONS = "pageSelections";
    const REASSIGN_TICKET_IDS = "reassignTicketIds";
    const hasReassignPermission = useSelector(state => (state.accountData.permissions || []).includes(Permissions.VIEW_MANAGE_TAB))
    const showReassignOption = hasReassignPermission && !props.filterState?.closedTicketsOnly && !props.isUnassignedQueue;
    const [reassignTicketIds, setReassignTicketIds] = useState([]);
    const [selectedTicketIds, setSelectedTicketIds] = useState({});
    const { associateId } = useParams('associateId');
    const storedSearchCriteria = JSON.parse(localStorage.getItem('ticket_search_criteria')) || {};
    const initialPage = storedSearchCriteria.associateSkip?.[associateId] || 0;
    const [currentPage, setCurrentPage] = useState(initialPage);
    const [pageSelections, setPageSelections] = useState({});

    const handleSelectAllTickets = (selectAll) => {
        const currentPageTickets = props.queue?.items || [];
        if (selectAll) {
            const allTicketIds = currentPageTickets.map((item) => item.ticketId);
            setReassignTicketIds((prevTicketIds) => [...prevTicketIds, ...allTicketIds]);
            setSelectedTicketIds((prevTicketIds) => ({
                ...prevTicketIds,
                [associateId]: [
                    ...(prevTicketIds[associateId] || []),
                    ...allTicketIds
                ]
            }));
            setPageSelections((prevSelections) => ({
                ...prevSelections,
                [associateId]: {
                    ...prevSelections[associateId],
                    [currentPage]: allTicketIds
                }
            }));              
        } else {
            const allTicketIds = currentPageTickets.map((item) => item.ticketId);
            setReassignTicketIds((prevTicketIds) => prevTicketIds.filter((id) => !allTicketIds.includes(id)));
            setSelectedTicketIds((prevTicketIds) => ({ ...prevTicketIds, [associateId]: [] }));
            setPageSelections((prevSelections) => ({
                ...prevSelections,
                [associateId]: {
                    ...prevSelections[associateId],
                    [currentPage]: []
                }
            }));              
        }
    };

    const handleCheckboxChange = (ticketId, newState) => {
        if (newState === true) {
            setReassignTicketIds((prevTicketIds) => [...prevTicketIds, ticketId]);
            setSelectedTicketIds((prevSelections) => ({
                ...prevSelections, [associateId]: [...(prevSelections[associateId] || []), ticketId]
            }));
            setPageSelections((prevSelections) => ({
                ...prevSelections,
                [associateId]: {
                    ...prevSelections[associateId], // Preserve previous selections for associateId
                    [currentPage]: [...(prevSelections[associateId]?.[currentPage] || []), ticketId] // Add the new ticketId to the currentPage array
                }
            }));
        } else {
            setReassignTicketIds((prevTicketIds) => prevTicketIds.filter((id) => id !== ticketId));
            setSelectedTicketIds((prevSelections) => ({
                ...prevSelections, [associateId]: (prevSelections[associateId] || []).filter((id) => id !== ticketId)
            }));
            setPageSelections((prevSelections) => ({
                ...prevSelections,
                [associateId]: {
                    ...prevSelections[associateId], // Preserve the previous selections for associateId
                    [currentPage]: (prevSelections[associateId]?.[currentPage] || []).filter((id) => id !== ticketId) // Filter the ticketId
                }
            }));
        }
    };

    useEffect(() => {
        setCurrentPage(initialPage);
        const totalCount = props.queue?.items?.length || localStorage.getItem('queueItemsCount');
        if (Object.keys(selectedTicketIds).length) {
            localStorage.setItem(REASSIGN_TICKET_IDS, JSON.stringify(selectedTicketIds));
            localStorage.setItem(PAGE_SELECTIONS, JSON.stringify(pageSelections));
        }
        const selectedTickets = pageSelections?.[associateId]?.[currentPage] || [];
        if (selectedTickets.length !== 0 && selectedTickets.length === Number(totalCount)) {
            setHeaderCheckboxState('checked');
        } else if (selectedTickets.length === 0) {
            setHeaderCheckboxState('unchecked');
        } else if (selectedTickets.length > 0 && selectedTickets.length < Number(totalCount)) {
            setHeaderCheckboxState('indeterminate');
        } else {
            setHeaderCheckboxState('indeterminate');
        }
    }, [currentPage, pageSelections, props.queue]);

    useEffect(() => {
        const storedSelectedTickets = JSON.parse(localStorage.getItem(REASSIGN_TICKET_IDS) || '{}');
        const storedPageSelections = JSON.parse(localStorage.getItem(PAGE_SELECTIONS) || '{}');

        const selectedTickets = selectedTicketIds?.[associateId]?.length
            ? selectedTicketIds
            : storedSelectedTickets;

        const pageSelection = Object.keys(pageSelections).length
            ? pageSelections
            : storedPageSelections;

        setReassignTicketIds(selectedTickets[associateId]?.map(Number) || []);
        setPageSelections(pageSelection);
        setSelectedTicketIds(selectedTickets);

        const parentAssociateId = localStorage.getItem('parentAssociateId');

        if (props.queue && !props.isAssociateQueue && (parentAssociateId !== String(associateId) || !props.officeId)) {
            setReassignTicketIds([]);
            selectedTicketIds[associateId] = [];
            pageSelections[associateId] = {};
        }
    }, [props.isAssociateQueue]);

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        const selectedTickets = pageSelections?.[associateId]?.[newPage] || [];
        if(Object.keys(pageSelections).length) {
            pageSelections[associateId][newPage] = selectedTickets;
        }
    };

    return (
        <div id='queue-table'>
            <QueueTableSortHeader {...props} showReassignOption={showReassignOption} reassignTicketIds={reassignTicketIds} pageSelections={pageSelections} />
            <QueueTableColumnHeader {...props} selectAllTickets={handleSelectAllTickets} headerCheckboxState={headerCheckboxState} showReassignOption={showReassignOption} />
            {props.queue == null &&
                <QueueTableLoader />
            }
            {props.queue && props.queue.items.length === 0 &&
                <QueueTableNoResults />
            }
            {props.queue && props.queue.items.length > 0 &&
                props.queue.items.map((t, i) =>
                    <QueueTableRow
                        ticket={t}
                        key={`qtr_${i}`}
                        {...props}
                        onCheckboxChange={(newState) => handleCheckboxChange(t.ticketId, newState)}
                        showReassignOption={showReassignOption}
                        isChecked={reassignTicketIds.includes(t.ticketId)}
                        reassignTicketIds={selectedTicketIds}
                        pageSelections={pageSelections}
                    />
                )
            }
            {props.queue && props.queue.totalCount > props.queue.items.length &&
                <QueueTablePages queue={props.queue} onSetOffset={(offset) => { props.onSetOffset(offset); handlePageChange(offset); }} />
            }
        </div>
    );
}

QueueTable.propTypes = {
    sort: PropTypes.string,
    onSortChanged: PropTypes.func,
    updateOrderBy: PropTypes.func,
    queue: PropTypes.object,
    onOpenTarget: PropTypes.func.isRequired,
    onSetOffset: PropTypes.func.isRequired,
    tableType: PropTypes.string.isRequired,
    isAssociateQueue: PropTypes.bool,
    associateFullName: PropTypes.string
}

export default QueueTable